/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';
import { Link } from '@mui/material';
import Button from '../../UI/Button';
import type { LinkDrawer, NavLink } from '.';
import { LogoPrimary } from '../../Icons';
import NavDrawer from '../Nav/NavDrawer';
import cx from 'classnames';
import { cn } from '../../../lib';

const DesktopNavLink: React.FC<
  NavLink & {
    active: boolean;
  }
> = ({ Component, ...props }) => {
  const { hasDrawer = false, active = false, name, to, label } = props;
  if (Component) {
    return (
      <Component
        className={cn(
          'relative my-[1px] flex h-full min-w-[64px] items-center whitespace-nowrap p-0 text-base after:absolute after:bottom-[-1px] after:h-[2px] after:w-full after:bg-transparent after:transition-colors after:content-[""]',
          {
            ' after:bg-cove-navy-blue': active,
          }
        )}
        {...props}
      />
    );
  }
  if (hasDrawer) {
    return (
      <Button
        className={cn(
          'relative my-[1px] flex h-full min-w-[64px] items-center whitespace-nowrap p-0 text-base font-normal after:absolute after:bottom-[-1px] after:h-[2px] after:w-full after:bg-transparent after:transition-colors after:content-[""]',
          {
            ' after:bg-cove-navy-blue': active,
          }
        )}
        variant="text"
        id={`nav-link-for-${name}`}
        cypressId={`nav-link-for-${name}`}
        href={to}
      >
        {label}
      </Button>
    );
  }
  return (
    <Button
      variant="text"
      id={`nav-link-for-${name}`}
      cypressId={`nav-link-for-${name}`}
      href={to}
      isUnderlined={false}
      className={cn(
        'relative my-[1px] flex h-full items-center whitespace-nowrap p-0 text-base font-normal after:absolute after:bottom-[-1px] after:h-[2px] after:w-full after:bg-transparent after:transition-colors after:content-[""]',
        {
          'after:bg-cove-navy-blue': active,
        }
      )}
    >
      {label}
    </Button>
  );
};

const DesktopNav: React.FC<{
  logoLink?: React.ReactNode | string;
  navLinks: NavLink[];
  linkDrawers: LinkDrawer[];
  clickToCall?: boolean;
  callNumber?: string;
  navLeft?: JSX.Element;
  navRight?: JSX.Element;
  showShopButton?: boolean;
}> = ({
  logoLink = '/',
  navLinks = [],
  linkDrawers = [],
  clickToCall = false,
  callNumber,
  navLeft,
  navRight,
  showShopButton = true,
  ...props
}) => {
  const [activeDrawer, setActiveDrawer] = useState('');
  const [activeLink, setActiveLink] = useState('');

  const handleMouseOver = (e, n) => {
    e.stopPropagation();
    setActiveDrawer(n);
    setActiveLink(n);
  };

  useEffect(() => {
    const callback = e => {
      e.stopPropagation();
      const domPath = (e?.path || e?.composedPath?.())?.map?.(p => p?.tagName);
      if (domPath && !domPath?.includes?.('NAV')) {
        setActiveDrawer('');
        setActiveLink('');
      }
    };

    document.body.addEventListener('mouseover', callback);

    return () => document.body.removeEventListener('mouseover', callback);
  }, []);

  return (
    <>
      <div
        className="px-cove-50 mx-auto flex h-[60px] w-full max-w-xl items-center justify-between"
        {...props}
      >
        {navLeft ? (
          <>{navLeft}</>
        ) : typeof logoLink === 'string' ? (
          <a
            href={logoLink}
            className="text-cove-green ml-cove-15 flex w-[91px] items-center lg:ml-0"
          >
            <LogoPrimary />
            <span className="sr-only">Go back to covesmart.com homepage</span>
          </a>
        ) : (
          <>{logoLink}</>
        )}

        {navRight ? (
          <>{navRight}</>
        ) : (
          <ul
            className="m-0 flex h-full list-none items-center font-normal"
            id="v1-nav"
          >
            {navLinks.map(l => {
              return (
                <li
                  className={cx(
                    'ml-cove-35 last:ml-cove-25 relative flex h-full items-center border-b border-b-transparent p-0 after:absolute after:bottom-[-1px] after:h-[2px] after:w-full after:bg-transparent after:transition-colors after:content-[""]',
                    {
                      ' after:bg-cove-navy-blue': activeLink === l.name,
                    }
                  )}
                  onMouseOver={e => handleMouseOver(e, l.name)}
                  key={l.name}
                >
                  <DesktopNavLink active={activeDrawer === l.name} {...l} />
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {linkDrawers.map((d, i) => {
        return (
          <NavDrawer
            key={`link-drawer-${i}`}
            active={activeDrawer === d.name}
            withDefaultStyle={d.withStyle}
            Component={d.Component}
          />
        );
      })}
    </>
  );
};

export default DesktopNav;
